import { useState, useEffect } from "react";
import { Wheel } from "react-custom-roulette";
import CelebrationModal from "./CelebrationModal.jsx";
import WheelTick from "../sounds/wheel-tick-v2.wav";
import PrizeWheelDisabled from "../images/PrizeWheelDisabled.svg";
import "../styles/prizeWheelVersionTwo.css";
import PropTypes from "prop-types";
import { trackModalLoad } from "../services/gtm/gtmTracker";
import { PRIZE_CELEBRATION_MODAL } from "../services/gtm/pageNames";

const pointer = {
  src: "/images/pointer.png",
  style: {
    transform: "rotate(-11deg) scale(1.15)",
    margin: "15px",
  },
};

const PrizeWheelVersionTwo = ({
  chosenIncentiveId,
  numberOfSpins,
  updatePromotionEarnings,
  payoutOptions,
  prizeWheelSoundFlag,
  getPayoutToken,
  updateSpinsForIncentive,
  toggle,
}) => {
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [prizeWheelDisabled, setPrizeWheelDisabled] = useState(false);
  const [processingData, setProcessingData] = useState(false);
  const [celebrationModalVisible, setCelebrationModalVisible] = useState(false);
  const [muteWheelSound, setMuteWheelSound] = useState(false);
  const [disableMuteButton, setDisableMuteButton] = useState(false);

  const handleSpinClick = async () => {
    const audio = new Audio(WheelTick);

    setProcessingData(true);
    setDisableMuteButton(true);
    if (!mustSpin) {
      const payoutAmount = await getPayoutToken(chosenIncentiveId);
      setPrizeNumber(
        payoutOptions.findIndex((o) => o.option === "$" + payoutAmount)
      );

      const newSpins = numberOfSpins - 1;
      updateSpinsForIncentive(chosenIncentiveId, newSpins);
      setCelebrationModalVisible(false);
      setMustSpin(true);

      if (prizeWheelSoundFlag && !muteWheelSound) {
        audio.play();
      }
    }
  };

  useEffect(() => {
    if (!processingData) {
      setPrizeWheelDisabled(numberOfSpins === 0);
    }
  }, [numberOfSpins, processingData]);

  return (
    <>
      <div className="prize-wheel-header-v2">
        <p className="updated-description-text-v2">
          Redeem your tokens to spin the wheel and earn prizes.
          <br />
          <button
            type="button"
            onClick={() => {
              toggle();
            }}
            className="link-button-v2"
            data-trackid="learn-more-promotion"
          >
            Learn more about this promotion.
          </button>
        </p>
        {prizeWheelSoundFlag && (
          <div>
            {!disableMuteButton ? (
              <button
                className="centered-mute-button-v2"
                type="button"
                data-trackid="toggle-wheel-sound"
                data-track-value={muteWheelSound ? "unmuted" : "muted"}
                onClick={() => {
                  setMuteWheelSound(!muteWheelSound);
                }}
              >
                {muteWheelSound ? (
                  <span
                    aria-label="Unmute wheel noise"
                    className="geico-icon geico-icon--actionable icon-muted-speaker"
                  ></span>
                ) : (
                  <span
                    aria-label="Mute wheel noise"
                    className="geico-icon geico-icon--actionable icon-speaker"
                  ></span>
                )}
              </button>
            ) : (
              <button
                type="button"
                className="centered-mute-button-v2"
                disabled
              >
                {muteWheelSound ? (
                  <span
                    aria-label="Disabled unmute wheel noise"
                    className="geico-icon icon-muted-speaker icon--gray"
                  ></span>
                ) : (
                  <span
                    aria-label="Disabled mute wheel noise"
                    className="geico-icon icon-speaker icon--gray"
                  ></span>
                )}
              </button>
            )}
          </div>
        )}
      </div>
      <div className="game-segment-v2" data-qm-allow="true">
        {!prizeWheelDisabled ? (
          <div className="wheel-v2" data-qm-allow="true">
            <div
              className="wheel-base-v2"
              data-testid="spinner-wheel-base-v2"
              data-qm-allow="true"
            >
              <Wheel
                data-qm-allow="true"
                mustStartSpinning={mustSpin}
                prizeNumber={prizeNumber}
                spinDuration={0.7}
                data={payoutOptions}
                radiusLineColor="white"
                radiusLineWidth={7}
                outerBorderWidth={2}
                outerBorderColor="white"
                perpendicularText={false}
                fontSize={28}
                textDistance={75}
                textColors={["white"]}
                backgroundColors={["#1D4A96", "#573E94", "#1D4A96", "#9380C7"]}
                pointerProps={pointer}
                onStopSpinning={() => {
                  setMustSpin(false);
                  setTimeout(() => {
                    trackModalLoad(PRIZE_CELEBRATION_MODAL);
                    setCelebrationModalVisible(true);
                    setProcessingData(false);
                    setDisableMuteButton(false);
                    const prizeAmount = parseFloat(
                      payoutOptions[prizeNumber].option.replace("$", "")
                    );
                    updatePromotionEarnings(chosenIncentiveId, prizeAmount);
                  }, 800);
                }}
              />
            </div>
            <div className="prize-wheel-button-v2">
              <button
                type="button"
                className="inner-circle-small-v2"
                onClick={handleSpinClick}
                disabled={processingData}
                data-trackid="push-to-spin"
              >
                <div
                  className="button-text-v2"
                  data-testid="prize-wheel-button-test-v2"
                >
                  <span className="button-text-top-v2">Push to</span>
                  <span className="button-text-bottom-v2">Spin!</span>
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="wheel-disabled-v2" data-testid="prize-wheel-disabled-test-v2">
            <div className="wheel-base-disabled-v2">
              <img src={PrizeWheelDisabled} className="wheel-disabled-image" alt="No Tokens Prize Wheel" />
            </div>
          </div>
        )}
      </div>
      {celebrationModalVisible && (
        <CelebrationModal prizeAmount={payoutOptions[prizeNumber].option} />
      )}
    </>
  );
};

PrizeWheelVersionTwo.propTypes = {
  chosenIncentiveId: PropTypes.string.isRequired,
  numberOfSpins: PropTypes.number.isRequired,
  updatePromotionEarnings: PropTypes.func.isRequired,
  payoutOptions: PropTypes.arrayOf(
    PropTypes.shape({
      option: PropTypes.string.isRequired,
    })
  ).isRequired,
  prizeWheelSoundFlag: PropTypes.bool.isRequired,
  getPayoutToken: PropTypes.func.isRequired,
  updateSpinsForIncentive: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
};

export default PrizeWheelVersionTwo;
